import type { ApiError } from "@circle-react/config/CustomErrors";
import {
  reactQueryDelete,
  reactQueryGet,
  reactQueryPatch,
  reactQueryPost,
} from "@circle-react/helpers/backendRequestHelpers";
import type {
  NotificationAction,
  NotificationPage,
} from "@circle-react/types/Notification";
import { noop } from "lodash";
import { useEffect, useEffectEvent } from "react";
import type { QueryClient } from "react-query";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";

import { notifications } from "@/react/helpers/urlHelpers/internalApi/notifications";

import {
  NOTIFICATIONS_PER_PAGE,
  getFetchNotificationQueryKey,
} from "./constants";
import { FILTERS, TABS } from "./NotificationContext";

const ACTION_API_MAPPER: any = {
  mark_as_read: ({ id }: any) =>
    reactQueryPatch(notifications.markAsRead({ id })),
  mark_all_as_read: () => reactQueryPost(notifications.markAllAsRead({})),
  follow: ({ postId }: any) => reactQueryPost(notifications.follow({ postId })),
  unfollow: ({ postId, followerId }: any) =>
    reactQueryDelete(notifications.unfollow({ postId, followerId })),
  archive: ({ id }: any) => reactQueryPatch(notifications.archive({ id })),
};

const NOTIFICATION_STALE_TIME = 60 * 1000;

export interface InvalidateNotificationsQueryCache {
  queryClient: QueryClient;
}

export const invalidateNotificationsQueryCache = ({
  queryClient,
}: InvalidateNotificationsQueryCache) => {
  void queryClient.invalidateQueries({ queryKey: ["notifications"] });
};

export const invalidateNotificationsCountQueryCache = ({
  queryClient,
}: InvalidateNotificationsQueryCache) => {
  void queryClient.invalidateQueries({ queryKey: ["notifications", "count"] });
};

export interface UseFetchNotifications {
  filter?: string;
  notificationGroup?: string;
  perPage?: number;
}

export const useFetchNotifications = ({
  perPage = NOTIFICATIONS_PER_PAGE,
  notificationGroup = TABS.INBOX,
  filter,
}: UseFetchNotifications) => {
  const {
    isInitialLoading: isLoading,
    data: notificationsData,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<NotificationPage>({
    initialPageParam: 1,
    queryKey: getFetchNotificationQueryKey(notificationGroup, filter),
    queryFn: async ({ pageParam = 1 }): Promise<NotificationPage> => {
      const requestParams: any = {
        per_page: String(perPage),
        notification_group: notificationGroup,
      };

      if (filter && filter !== FILTERS.ALL) {
        requestParams.status = filter;
      }

      // pageParam is always a page number
      if (pageParam === 1) {
        // First page
        requestParams.page = pageParam;
      } else {
        // For subsequent pages, check if the previous page has a cursor
        const previousPage = notificationsData?.pages[pageParam - 2];
        if (previousPage?.next_search_after) {
          // Use cursor-based pagination
          requestParams.next_search_after = previousPage.next_search_after;
          requestParams.page = pageParam;
        } else {
          // Fall back to page-based pagination
          requestParams.page = pageParam;
        }
      }

      return reactQueryGet(notifications.fetch(requestParams));
    },
    staleTime: NOTIFICATION_STALE_TIME,
    getNextPageParam: lastPage =>
      lastPage.has_next_page ? lastPage.page + 1 : undefined,
  });

  const lastPage =
    notificationsData?.pages[notificationsData?.pages.length - 1];
  const hasNextPage = !!lastPage?.has_next_page;

  return {
    isLoading,
    isFetchingNextPage,
    notifications: notificationsData,
    refetch,
    fetchNextPage,
    hasNextPage,
  };
};

export const useNotificationsCount = (
  communityId: any,
  setNotificationTabCounters: any,
) => {
  const query = useQuery({
    queryKey: ["notifications", "count", communityId],
    queryFn: () =>
      reactQueryGet(
        notifications.fetchNotificationCount({ community_id: communityId }),
      ),
    enabled: !!communityId,
  });

  const applyCounters = useEffectEvent(
    (counts: {
      new_inbox_count: number;
      new_mentions_count: number;
      new_notifications_count: number;
    }) => {
      setNotificationTabCounters({
        inbox: counts.new_inbox_count,
        mentions: counts.new_mentions_count,
        notifications: counts.new_notifications_count,
      });
    },
  );

  useEffect(() => {
    if (!query.data) return;
    applyCounters(query.data);
  }, [query.data]);

  return query;
};

export const useResetNotificationCount = (setNotificationCount: any) => {
  const { mutate } = useMutation({
    mutationFn: () => reactQueryPost(notifications.resetNotificationCount()),
    onSuccess: data => {
      setNotificationCount(data.new_notifications_count);
    },
  });
  return { mutate };
};

export const useNotificationsActions = (
  onSuccess: (data: any) => void = noop,
) => {
  const queryClient = useQueryClient();

  const { mutate, isPending: isLoading } = useMutation<
    NotificationAction,
    ApiError,
    NotificationAction
  >({
    mutationFn: ({ action, id, postId, followerId }) =>
      ACTION_API_MAPPER[action]({ id, postId, followerId }),
    onSuccess: data => {
      invalidateNotificationsQueryCache({ queryClient });
      onSuccess(data);
    },
  });

  return { mutate, isLoading };
};
